import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { Instagram, MessageCircle, CalendarPlus, CheckCircle2, XCircle, Clock, TrendingUp, Flame } from "lucide-react";
import { getMetrics } from "@/lib/dentiumApi";

const RANGES = [
  { key: "today", label: "Hoy" },
  { key: "7d", label: "Últimos 7 días" },
  { key: "30d", label: "Últimos 30 días" },
  { key: "month", label: "Este mes" },
];

const CARDS = [
  { key: "instagram_messages", label: "Mensajes por Instagram", icon: Instagram, color: "#E1306C", bg: "#FCEEF4" },
  { key: "whatsapp_messages", label: "Mensajes por WhatsApp", icon: MessageCircle, color: "#1FA855", bg: "#E9F8EF" },
  { key: "citas_solicitadas", label: "Citas solicitadas", icon: CalendarPlus, color: "#1A1A1A", bg: "#F1EBE1" },
  { key: "citas_confirmadas", label: "Citas confirmadas", icon: CheckCircle2, color: "#047857", bg: "#E9F8EF" },
  { key: "citas_canceladas", label: "Citas canceladas", icon: XCircle, color: "#DC2626", bg: "#FDECEC" },
  { key: "citas_pendientes", label: "Citas pendientes", icon: Clock, color: "#B45309", bg: "#FBF3E6" },
];

const dayLabel = (iso) => {
  try { return new Date(iso + "T12:00:00").toLocaleDateString("es-ES", { day: "2-digit", month: "short" }); }
  catch { return iso; }
};

export default function MetricasPage() {
  const [range, setRange] = useState("7d");
  const { data: m = {} } = useQuery({ queryKey: ["metrics", range], queryFn: () => getMetrics(range), refetchInterval: 10000 });

  const series = (m.series || []).map((s) => ({ ...s, label: dayLabel(s.date) }));
  const confirmRate = m.confirm_rate;
  const cancelRate = m.cancel_rate;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1100px] mx-auto overflow-y-auto h-full" data-testid="metricas-page">
      {/* Selector de rango */}
      <div className="flex items-center gap-2 flex-wrap mb-6" data-testid="metrics-range-selector">
        {RANGES.map((r) => (
          <button
            key={r.key}
            data-testid={`range-${r.key}`}
            onClick={() => setRange(r.key)}
            className={`h-9 px-4 rounded-full text-sm font-medium border transition-colors ${
              range === r.key ? "bg-[#1A1A1A] text-white border-[#1A1A1A]" : "bg-white text-[#6B6157] border-[#E7E2DA] hover:bg-[#F1EBE1]"
            }`}
          >
            {r.label}
          </button>
        ))}
      </div>

      {/* Tarjetas grandes */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {CARDS.map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.key} data-testid={`metric-${c.key}`} className="rounded-3xl border border-[#E7E2DA] bg-white p-6 flex flex-col gap-4">
              <span className="h-14 w-14 rounded-2xl flex items-center justify-center" style={{ background: c.bg, color: c.color }}>
                <Icon className="h-7 w-7" />
              </span>
              <div>
                <div className="font-display text-5xl font-normal leading-none" style={{ color: c.color }} data-testid={`metric-value-${c.key}`}>
                  {m[c.key] ?? 0}
                </div>
                <div className="text-sm font-medium text-[#6B6157] mt-2">{c.label}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pico de actividad + Confirmadas vs Canceladas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-5">
        <div className="rounded-3xl border border-[#E7E2DA] bg-white p-6" data-testid="metric-peak">
          <div className="flex items-center gap-2 mb-2">
            <span className="h-10 w-10 rounded-xl flex items-center justify-center bg-[#FBF3E6] text-[#B45309]"><Flame className="h-5 w-5" /></span>
            <h3 className="text-base font-medium text-[#1A1A1A]">Horario de mayor actividad</h3>
          </div>
          {m.peak ? (
            <>
              <div className="font-display text-3xl text-[#1A1A1A] capitalize mt-2">{m.peak.label}</div>
              <p className="text-sm text-[#8A7E70] mt-1">{m.peak.count} mensajes en esa franja</p>
            </>
          ) : (
            <p className="text-sm text-[#A79E90] mt-3">Aún no hay suficientes datos en este rango.</p>
          )}
        </div>

        <div className="rounded-3xl border border-[#E7E2DA] bg-white p-6" data-testid="metric-confirm-rate">
          <div className="flex items-center gap-2 mb-3">
            <span className="h-10 w-10 rounded-xl flex items-center justify-center bg-[#E9F8EF] text-emerald-700"><TrendingUp className="h-5 w-5" /></span>
            <h3 className="text-base font-medium text-[#1A1A1A]">Citas confirmadas vs canceladas</h3>
          </div>
          {confirmRate === null || confirmRate === undefined ? (
            <p className="text-sm text-[#A79E90] mt-3">Sin citas confirmadas o canceladas en este rango.</p>
          ) : (
            <>
              <div className="flex items-end gap-6 mt-2">
                <div>
                  <div className="font-display text-4xl text-emerald-600 leading-none">{confirmRate}%</div>
                  <div className="text-xs text-[#6B6157] mt-1">Confirmadas</div>
                </div>
                <div>
                  <div className="font-display text-4xl text-red-600 leading-none">{cancelRate}%</div>
                  <div className="text-xs text-[#6B6157] mt-1">Canceladas</div>
                </div>
              </div>
              <div className="mt-4 h-3 rounded-full overflow-hidden bg-red-100 flex">
                <div className="bg-emerald-500 h-full" style={{ width: `${confirmRate}%` }} />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Gráfico de evolución */}
      <div className="rounded-3xl border border-[#E7E2DA] bg-white p-6 mt-5" data-testid="metrics-chart">
        <h3 className="text-base font-medium text-[#1A1A1A] mb-4">Evolución de mensajes recibidos por día</h3>
        <div style={{ width: "100%", height: 300 }}>
          <ResponsiveContainer>
            <BarChart data={series} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EDE7DE" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#8A7E70" }} tickLine={false} axisLine={{ stroke: "#E7E2DA" }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#8A7E70" }} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E7E2DA", fontSize: 13 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="whatsapp" name="WhatsApp" stackId="a" fill="#25D366" radius={[0, 0, 0, 0]} />
              <Bar dataKey="instagram" name="Instagram" stackId="a" fill="#E1306C" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
